























































































































import { Component, Vue } from 'vue-property-decorator';
import BaseModal from '@/components/atoms/BaseModal.vue';
import SkillsCompleted from '@/components/molecules/SkillsCompleted.vue';
import CourseCardELearning from '@/components/organisms/CourseCardELearning.vue';
import CourseCardWebinar from '@/components/organisms/CourseCardWebinar.vue';
import CourseCardClassroom from '@/components/organisms/CourseCardClassroom.vue';
import CourseCardLearningPlan from '@/components/organisms/CourseCardLearningPlan.vue';
import NewcrossButton from '@/components/atoms/NewcrossButton.vue';
import NewcrossTab from '@/components/atoms/NewcrossTab.vue';
import HTMLContainer from '@/components/atoms/HTMLContainer.vue';
import DidYouKnow from '@/components/molecules/DidYouKnow.vue';
import {
  DoceboCourseType,
  DoceboCatalogueView,
  DoceboCatalogueViewItem,
  DoceboCatalogueViewLearningPlan,
  DoceboEnrolmentItemLearningPlan,
  DoceboEnrolmentItemCourse,
} from '@/lib/DoceboAPI';
import { Building, Catalogue } from '@/types';
import { HealthForceGoSkill } from '@/lib/HealthForceGoAPI';
import calculateExpiryStatus, {
  ExpiryStatus,
} from '@/lib/calculateExpiryStatus';

@Component({
  components: {
    BaseModal,
    SkillsCompleted,
    CourseCardELearning,
    CourseCardWebinar,
    CourseCardClassroom,
    CourseCardLearningPlan,
    NewcrossButton,
    NewcrossTab,
    DidYouKnow,
    HTMLContainer,
  },
})
export default class BuildingDetail extends Vue {
  private DoceboCourseType = DoceboCourseType; // Expose DoceboCourseType enum to template

  private get buildingId(): string {
    return this.$route.params.buildingId;
  }

  private get building(): Building {
    return this.$store.state.config.buildings.find(
      (building: Building) => building.id === this.buildingId
    );
  }

  private get filteredBuildingName(): string {
    if (!this.mandatoryCatalogueView) return '';
    const title = this.mandatoryCatalogueView.extra_data.name;
    return title.split('|')[0];
  }

  private get catalogues(): Catalogue[] {
    return this.$store.getters[
      'catalogues/getConditionalCataloguesForBuilding'
    ](this.buildingId);
  }

  private get catalogueId(): number {
    return Number(this.$route.params.catalogueId);
  }

  private get activeCatalogueView(): DoceboCatalogueView {
    return this.$store.state.catalogues.catalogueViews[this.catalogueId];
  }

  private get activeEnrolments(): {
    learningPlanEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemLearningPlan;
    };
    courseEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemCourse;
    };
  } {
    if (!this.building)
      return {
        learningPlanEnrolments: {},
        courseEnrolments: {},
      };
    return this.$store.getters['enrolments/getEnrolmentStatusesByCatalogueId'](
      this.catalogueId
    );
  }

  private get mandatoryCatalogueId(): number | undefined {
    const mandatoryCatalogueId = this.catalogues.find(
      (catalogue) => catalogue.mandatory
    )?.doceboCatalogueId;
    return mandatoryCatalogueId || undefined;
  }

  private get mandatoryCatalogueView(): DoceboCatalogueView | undefined {
    if (this.mandatoryCatalogueId) {
      return this.$store.state.catalogues.catalogueViews[
        this.mandatoryCatalogueId
      ];
    }
    return undefined;
  }

  private get mandatoryEnrolments(): {
    learningPlanEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemLearningPlan;
    };
    courseEnrolments: {
      [enrolmentId: string]: DoceboEnrolmentItemCourse;
    };
  } {
    if (!this.mandatoryCatalogueId)
      return {
        learningPlanEnrolments: {},
        courseEnrolments: {},
      };
    return this.$store.getters['enrolments/getEnrolmentStatusesByCatalogueId'](
      this.mandatoryCatalogueId
    );
  }

  private get completedMandatoryCourses(): number {
    let completed = 0;
    Object.keys(this.mandatoryEnrolments.courseEnrolments).forEach(
      (enrolmentId) => {
        if (
          this.mandatoryEnrolments.courseEnrolments[enrolmentId].status ===
          'completed'
        )
          completed += 1;
      }
    );
    Object.keys(this.mandatoryEnrolments.learningPlanEnrolments).forEach(
      (enrolmentId) => {
        if (
          this.mandatoryEnrolments.learningPlanEnrolments[enrolmentId]
            .status === 'completed'
        )
          completed += 1;
      }
    );
    return completed;
  }

  private get totalMandatoryCourses(): number {
    return this.mandatoryCatalogueView?.data.items.length || 0;
  }

  private setTab(doceboCatalogueId: number) {
    this.$router
      .push({
        name: 'Building',
        params: {
          buildingId: this.building.id,
          catalogueId: doceboCatalogueId.toString(),
        },
        hash: this.$route.hash, // We aren't changing page, so we need to supply this else the hash gets lost
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }

  private get activeCourses(): DoceboCatalogueViewItem[] {
    if (
      !this.activeCatalogueView ||
      (!this.activeEnrolments.learningPlanEnrolments &&
        !this.activeEnrolments.courseEnrolments)
    )
      return [];
    return this.activeCatalogueView.data.items
      .sort(this.sortByPriority)
      .reverse();
  }

  private courseEnrolmentPriority: { [key: string]: number } = {
    expired: 8, // expiry
    'expiring-soon': 7, // expiry
    'in-progress': 6, // enrolment
    enrolled: 5, // enrolment
    waiting: 4, // enrolment
    'no-enrolment': 3, // no expiry or enrolment
    expiring: 2, // expiry
    completed: 1, // enrolment
    locked: 0, // enrolment
    incomplete: -1, // expiry, unused
  };

  private sortByPriority(
    a: DoceboCatalogueViewItem,
    b: DoceboCatalogueViewItem
  ): number {
    const aPriority = this.getPriorityforItem(a);
    const bPriority = this.getPriorityforItem(b);
    // Sort by priority
    if (aPriority < bPriority) return -1;
    if (aPriority > bPriority) return 1;
    // Sort by expiry date
    const aSkills = this.getCatalogueViewItemSkill(a);
    const bSkills = this.getCatalogueViewItemSkill(b);
    const aExpiry = this.getSkillExpiry(aSkills);
    const bExpiry = this.getSkillExpiry(bSkills);
    if (aExpiry && !bExpiry) return 1;
    if (!aExpiry && bExpiry) return -1;
    if (aExpiry && bExpiry && aExpiry > bExpiry) return -1;
    if (aExpiry && bExpiry && bExpiry < aExpiry) return 1;
    // Sort alphabetically
    if (a.item_name > b.item_name) return -1;
    if (a.item_name < b.item_name) return 1;
    // Nothing left to sort by
    return 0;
  }

  private getPriorityforItem(item: DoceboCatalogueViewItem): number {
    const skills = this.getCatalogueViewItemSkill(item);
    const expiryStatus = calculateExpiryStatus(skills);
    const enrolment =
      item.course_type === DoceboCourseType.LEARNING_PLAN
        ? this.activeEnrolments.learningPlanEnrolments[item.item_id]
        : this.activeEnrolments.courseEnrolments[item.item_id];
    // prioritise expiry over enrolment status
    if (
      [
        ExpiryStatus.EXPIRED,
        ExpiryStatus.EXPIRING_SOON,
        ExpiryStatus.EXPIRING,
      ].includes(expiryStatus)
    ) {
      return this.courseEnrolmentPriority[expiryStatus];
    }
    if (enrolment) return this.courseEnrolmentPriority[enrolment.status];
    return this.courseEnrolmentPriority['no-enrolment'];
  }

  private getCatalogueViewItemSkill(
    catalogueViewItem: DoceboCatalogueViewItem
  ): HealthForceGoSkill[] {
    const skills: HealthForceGoSkill[] =
      catalogueViewItem.course_type === DoceboCourseType.LEARNING_PLAN
        ? this.$store.getters['skills/getSkillsForLearningPlan'](
            catalogueViewItem.item_id
          )
        : this.$store.getters['skills/getSkillsForCourse'](
            catalogueViewItem.item_id
          );
    return skills;
  }

  // eslint-disable-next-line class-methods-use-this
  private getSkillExpiry(skills: HealthForceGoSkill[]): number | null {
    if (!skills || !skills.length) return null;
    return skills[0].expiryDate
      ? new Date(skills[0].expiryDate).getTime()
      : null;
  }

  private openLearningPlan(learningPlan: DoceboCatalogueViewLearningPlan) {
    this.$router
      .push({
        name: 'Learning Plan',
        params: {
          buildingId: this.buildingId,
          catalogueId: this.catalogueId.toString(),
          learningPlanId: learningPlan.item_id,
        },
      })
      .catch((e) => {
        console.assert(true, e);
      });
  }
}
